import useSWR, { mutate } from "swr";
import { fetcher, postFetcher } from "./fetcher";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useParams } from "next/navigation";
import { PortfolioComputeActions, usePortfolioCompute } from "lib/analytics/react/hooks";


export const useSWRFetcher = (url: string, params: any, key: any, method = 'POST') => {

    const { data, isValidating, mutate } = useSWR(
        key ? [url, key] : null,
        method === 'POST' ? () => postFetcher(url, params) : fetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );
    const isValid = !data && isValidating;
    return [data, isValid, mutate, isValidating];
};

export const usePortfolioRisk = (PORT_ID: number) => {
    return useSWRFetcher(`/api/portfolio/portfolioRisk`, { PORT_ID }, PORT_ID);
};
export const usePortfolioHistory = (PORT_ID: number, type: string) => {
    return useSWRFetcher(`/api/portfolio/history?type=${type}`, { PORT_ID, type }, PORT_ID);
};
export const usePortfolioAssetRiskChange = (PORT_ID: number, TARGET_DATE: any) => {
    return useSWRFetcher(`/api/portfolio/assetRiskChange`, { PORT_ID, TARGET_DATE }, PORT_ID);
};
export const usePortfolioStatisitcs = (PORT_ID: number, type: string, CVaR_LV: string, TOTAL_AMOUNT: number) => {
    return useSWRFetcher(`/api/portfolio/statisitcs?type=${type}`, { PORT_ID, type, CVaR_LV, TOTAL_AMOUNT }, PORT_ID);
};
export const usePortfolioCompare = (PORT_ID: number, CVaR_LV: string, TOTAL_AMOUNT: number) => {
    const key = PORT_ID ? `${PORT_ID}_${CVaR_LV || TOTAL_AMOUNT}` : null;
    return useSWRFetcher(`/api/portfolio/compare`, { PORT_ID, CVaR_LV, TOTAL_AMOUNT }, key);
};
export const usePortfolioSharpe = (PORT_ID: number | null) => {
    return useSWRFetcher(`/api/portfolio/sharpe`, { PORT_ID }, PORT_ID);
}




///
// 투자매력도
export const usePortfolioScore = (PORT_ID: number, hashId: string | null | undefined) => {
    const { data: session, status }: any = useSession();
    const email = session?.user?.email;
    const PORT_ID_TYPE_IS_NUMBER = typeof PORT_ID === "number";
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfScore", PORT_ID, session?.user?.email ? null : hashId],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/score`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID: !email || !PORT_ID_TYPE_IS_NUMBER ? null : PORT_ID, hashId }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await res.json();
            return data;
        },
        // enabled: !!PORT_ID,
        staleTime: 1000 * 60,
        enabled: hashId !== undefined && !!(hashId || PORT_ID) && status !== "loading",
    })
    return { data, isLoading, isError };
}

// 투자매력도 비교
export const usePortfolioScoreCompare = () => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfScoreCompare"],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/scoreCompare`)
            const data = await res.json();
            return data;
        }
    })
    return { data, isLoading, isError };
}


// 유사도
export const usePortfolioSimilarity = (PORT_ID: number, hashId: string | null | undefined) => {
    const { data: session, status }: any = useSession();
    const email = session?.user?.email;
    const PORT_ID_TYPE_IS_NUMBER = typeof PORT_ID === "number";
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfSimilarity", PORT_ID, session?.user?.email ? null : hashId],
        queryFn: async () => {

            const res = await fetch(`/api/portfolio/similarity`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID: !email || !PORT_ID_TYPE_IS_NUMBER ? null : PORT_ID, hashId }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await res.json();
            return data;
        },
        staleTime: 1000 * 60,
        enabled: hashId !== undefined && !!(hashId || PORT_ID) && status !== "loading",
    })
    return { data, isLoading, isError };
}


// 변동성
export const usePortfolioVar = (PORT_ID: number, hashId: string | null | undefined) => {
    const { data: session, status }: any = useSession();
    const email = session?.user?.email;
    const PORT_ID_TYPE_IS_NUMBER = typeof PORT_ID === "number";
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfVar", PORT_ID, session?.user?.email ? null : hashId],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/risk`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID: !email || !PORT_ID_TYPE_IS_NUMBER ? null : PORT_ID, hashId }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await res.json();
            return data;
        },
        staleTime: 1000 * 60,
        enabled: hashId !== undefined && !!(hashId || PORT_ID) && status !== "loading",
    })
    return { data, isLoading, isError };
}

// 뉴스레이더
export const usePortfolioNews = (PORT_ID: number, hashId: string | null | undefined) => {
    const { data: session, status }: any = useSession();
    const email = session?.user?.email;
    const PORT_ID_TYPE_IS_NUMBER = typeof PORT_ID === "number";
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfNews", PORT_ID, hashId],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/news`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID: !email || !PORT_ID_TYPE_IS_NUMBER ? null : PORT_ID, hashId }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await res.json();
            return data;
        },
        staleTime: 1000 * 60,
        enabled: hashId !== undefined && !!(hashId || PORT_ID) && status !== "loading",
    })
    return { data, isLoading, isError };
}

// 고투자매력도 종목 리스트
export const usePortfolioHighScoreStocks = (PORT_ID: number,) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfHighScoreStocks", PORT_ID],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/highscore`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await res.json();
            return data;
        },
        staleTime: 1000 * 60,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError };
}

// 분산도 종목 리스트
export const usePortfolioVarianceStocks = (PORT_ID: number,) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfVarianceStocks", PORT_ID],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/variance`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                e.status = res.status;
                Sentry.captureException(e);
                throw e; // <= 이걸로 isError가 true로 변함
            }
            const data = await res.json();
            return data;
        },
        staleTime: 1000 * 60,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError };

}

// 최적화
export const usePortfolioOptimize = (PORT_ID: number, type: string) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfOptimize", PORT_ID, type],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/optimize`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID, type }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                e.status = res.status;
                Sentry.captureException(e);
                throw e; // <= 이걸로 isError가 true로 변함
            }
            const data = await res.json();
            const chg = data?.change?.after - data?.change?.before;
            if ((chg > 0 && type === "downsize") || (chg < 0 && type === "upsize")) {

                Sentry.captureMessage("변동성 최적화 결과 오류 - 변동성 최적화 결과가 예상과 다름", {
                    extra: {
                        data,
                        type,
                    },
                });
                throw new Error("변동성 최적화 결과 오류");
            }
            return data;
        },
        staleTime: 1000 * 60,
        retry: false,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError };
}
// 포트폴리오 가격
export const usePortfolioPrice = (PORT_ID: number) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfPrice", PORT_ID],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/price`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (res.status === 404) {
                return null;
            }
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                e.status = res.status;
                Sentry.captureException(e);
                throw e; // <= 이걸로 isError가 true로 변함
            }
            const data = await res.json();
            return data;
        },
        staleTime: 0,
        retry: false,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError };
}

// 회원 포트폴리오 보유 수량+원금 정보
export const usePortfolioHolding = (PORT_ID: number) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["pfHolding", PORT_ID],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/holding`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                e.status = res.status;
                Sentry.captureException(e);
                throw e; // <= 이걸로 isError가 true로 변함
            }
            const data = await res.json();
            return data;
        },
        staleTime: 0,
        retry: false,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError };
}

// 비회원 보유 수량 및 아이템 정보 -get localstorage
export const usePortfolioNonMemberHolding = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState<any>(null);
    const [finalData, setFinalData] = useState<any>(null);
    const { data: itemInfo, isLoading: isItemInfoLoading, isError: isItemInfoError } = useQuery({
        queryKey: ["itemInfo"],
        queryFn: async () => {
            const query = new URLSearchParams();
            data?.forEach((item: any) => {
                query.append("ticker", item.ticker);
            });
            const res = await fetch(`/api/iteminfo?${query.toString()}`);
            return await res.json();
        },

        enabled: !!data,
        staleTime: 0,
    })


    useEffect(() => {
        const hashId = localStorage.getItem("pf-hashId");
        if (hashId) {
            const data = localStorage.getItem(`pf-${hashId}`);
            if (data) {
                setData(JSON.parse(data)?.items);
            } else {
                setIsError(true);
            }
        } else {
            setIsError(true);
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (itemInfo && data) {
            console.log("itemInfo", itemInfo);
            const finData = data?.map((item: any) => {
                const targetData = itemInfo?.find((itemInfo: any) => String(itemInfo.ITEM_CD_DL) === String(item.ticker));
                console.log("targetData", targetData);

                const format = {
                    itemCd: targetData?.ITEM_CD,
                    name: targetData?.ITEM_ENG_NM,
                    krName: targetData?.ITEM_KR_NM,
                }

                return {
                    ...item,
                    ...format,
                }
            })
            setFinalData(finData);
        }
    }, [itemInfo, data]);

    return { data: finalData, isLoading: isLoading || isItemInfoLoading, isError: isError || isItemInfoError };
}



// 포트폴리오 마지막 접속 시간
export const usePortfolioLastView = () => {
    const { data: session } = useSession();
    const { mutate, isError } = useMutation({
        mutationKey: ["pfLastView"],
        mutationFn: async () => {
            const res = await fetch(`/api/portfolio/lastView`, {
                method: "POST",
            })
            const data = await res.json();
            return data;
        },

    })

    useEffect(() => {
        if (session?.user?.email) {
            mutate();
        }
    }, [session]);

}

// 포트폴리오 추천(처방) 전체
export const usePortfolioRecommandResult = (PORT_ID: number) => {
    const { data, isLoading, isError, isPending, isFetching } = useQuery({
        queryKey: ["pfRecommand", PORT_ID],
        queryFn: async () => {
            const res = await fetch(`/api/portfolio/recommand/result`, {
                method: "POST",
                body: JSON.stringify({ PORT_ID }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                e.status = res.status;
                Sentry.captureException('포트폴리오 처방 데이터 조회 실패', {
                    extra: {
                        error: e,
                        PORT_ID,
                    },
                });
                throw e; // <= 이걸로 isError가 true로 변함
            }

            const data = await res.json();

            if (!data.optimize || !data.similarity || !data.ranking || !data.survey) {
                Sentry.captureMessage("포트폴리오 처방 데이터 누락 - 추천 결과가 없음", {
                    extra: {
                        data,
                        PORT_ID,
                    },
                });
            }
            return data;
        },
        staleTime: 0,
        retry: false,
        enabled: !!PORT_ID,
    })
    return { data, isLoading, isError, isPending, isFetching };
}

// 포트폴리오 서베이 적재
export const usePortfolioSurvey = () => {
    const router = useRouter();
    const { portId } = useParams();
    const queryClient = useQueryClient();
    const portfolioCompute = usePortfolioCompute({ type: "diagnosis" });

    const { mutate, isPending, isError } = useMutation({
        mutationKey: ["pfSurvey", Number(portId)],
        mutationFn: async (answer: any) => {
            portfolioCompute.start({
                portfolio_id: Number(portId),
            });
            const res = await fetch(`/api/portfolio/recommand/survey`, {
                method: "POST",
                body: JSON.stringify(answer),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {

                const body = await res.json().catch(() => null);
                const e: any = new Error(body?.message || `HTTP ${res.status}`);
                portfolioCompute.fail("backend_" + res.status);
                e.status = res.status;
                Sentry.captureException('포트폴리오 서베이 적재 실패', {
                    extra: {
                        error: e,
                        answer,
                    },
                });
                throw e;
            }
            const data = await res?.json();
            return data;
        },
        onSuccess: async () => {
            portfolioCompute.success({ portfolio_id: Number(portId) });
            console.log("onSuccess 무효화", portId);

            const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
            await sleep(1000);

            const numericPortId = Number(portId);
            if (!Number.isNaN(numericPortId)) {
                queryClient.removeQueries({ queryKey: ["pfRecommand", numericPortId] });
                await queryClient.invalidateQueries({ queryKey: ["pfRecommand", numericPortId] });
            } else {
                await queryClient.invalidateQueries({ queryKey: ["pfRecommand"] });
            }
            router.push(`/portfolio/${portId}/recommand/result`);
        },

    })
    return { mutate, isPending, isError };
}